import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { InfoButton } from './InfoModal';
import toast from 'react-hot-toast';
import { Pencil } from 'lucide-react';
import { sortSsCodeStrings } from '../utils/ssCodeSort';

function isPartialCoverage(building) {
  const agency = String(building?.agency_scope || '').toUpperCase();
  const scope = String(building?.inspection_scope || '').toUpperCase();
  return agency === 'SS_ONLY' || scope === 'PARTIAL';
}

function buildingSsCodes(building) {
  const rows = building?.ssInstances || building?.ss_instances || [];
  return sortSsCodeStrings(
    rows
      .filter((row) => !row.status || row.status === 'ACTIVE')
      .map((row) => row.ss_code)
      .filter(Boolean)
      .map(String)
  );
}

function codesOf(mandate) {
  if (Array.isArray(mandate?.display_ss_codes) && mandate.display_ss_codes.length) {
    return mandate.display_ss_codes.map(String);
  }
  const assignments = Array.isArray(mandate?.ss_assignments) ? mandate.ss_assignments : [];
  return assignments.map((row) => row?.ss_code).filter(Boolean).map(String);
}

/**
 * Full agent only, and only for SS this company does not cover.
 * Partial coverage hides the section. A full-agent building with no outside SS hides it too.
 */
export default function BuildingMandatesSection({ buildingId, canManage, building }) {
  const queryClient = useQueryClient();

  const { data: mandates = [], isLoading } = useQuery({
    queryKey: ['building', buildingId, 'mandates'],
    queryFn: async () => {
      const raw = (await api.get(`/mandates/buildings/${buildingId}`)).data?.data;
      return Array.isArray(raw) ? raw : [];
    },
    enabled: !!buildingId && !isPartialCoverage(building),
  });

  const { data: partners = [] } = useQuery({
    queryKey: ['partner-companies'],
    queryFn: async () => (await api.get('/partner-companies')).data?.data || [],
    enabled: !!buildingId && canManage && !isPartialCoverage(building),
  });

  const invalidate = () => {
    queryClient.invalidateQueries(['building', buildingId, 'mandates']);
    queryClient.invalidateQueries(['mandates', 'dashboard']);
  };

  const saveName = useMutation({
    mutationFn: async ({ mandateId, partnerId, ssCodes }) => {
      if (mandateId) {
        await api.put(`/mandates/${mandateId}`, { partner_company_id: partnerId });
        return;
      }
      await api.post(`/mandates/buildings/${buildingId}`, {
        mandate_type: 'SS_ONLY',
        partner_company_id: partnerId,
        ss_assignments: ssCodes.map((ss_code) => ({ ss_code })),
      });
    },
    onSuccess: () => {
      toast.success('Agent updated');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message || 'Could not save the agent'),
  });

  if (isPartialCoverage(building) || isLoading) return null;

  const ours = mandates.find((row) => row.is_our_agent);
  const ourCodes = new Set(codesOf(ours));
  const otherMandates = mandates.filter((row) => !row.is_our_agent && codesOf(row).length);
  const covered = new Set(otherMandates.flatMap((row) => codesOf(row)));
  const outside = buildingSsCodes(building).filter(
    (code) => ourCodes.size > 0 && !ourCodes.has(code) && !covered.has(code)
  );

  if (otherMandates.length === 0 && outside.length === 0) return null;

  const rows = [
    ...otherMandates.map((row) => ({
      key: `m-${row.id}`,
      mandateId: row.id,
      ssCodes: sortSsCodeStrings(codesOf(row)),
      partnerId: row.partner_company_id ? String(row.partner_company_id) : '',
      name: row.display_company_name || row.partner_name || row.external_party_name || '',
    })),
    ...(outside.length
      ? [
          {
            key: 'outside',
            mandateId: null,
            ssCodes: outside,
            partnerId: '',
            name: '',
          },
        ]
      : []),
  ];

  return (
    <div id="service-mandates" className="bg-card rounded-lg shadow p-6 mb-6 scroll-mt-24">
      <div className="flex items-baseline gap-2 mb-4 flex-wrap">
        <h2 className="text-xl font-semibold text-gray-900">Service mandates</h2>
        <InfoButton title="Service mandates">
          <p>
            Shown only when this building is <strong>Full agent</strong> and some Specified Systems are not
            ones we cover. Those SS are listed here with the agent who holds them. Pick a registered company,
            or open Edit to change the name.
          </p>
        </InfoButton>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-green-100">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500">SS</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Agent</th>
              {canManage ? <th className="px-3 py-2 text-right font-medium text-gray-500">Actions</th> : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.key} className="bg-green-100">
                <td className="px-3 py-2 text-gray-900">{row.ssCodes.join(', ')}</td>
                <td className="px-3 py-2 text-gray-900">
                  {canManage ? (
                    <select
                      value={row.partnerId}
                      disabled={saveName.isPending}
                      onChange={(e) => {
                        const partnerId = e.target.value;
                        if (!partnerId) return;
                        saveName.mutate({
                          mandateId: row.mandateId,
                          partnerId,
                          ssCodes: row.ssCodes,
                        });
                      }}
                      className="w-full max-w-xs px-2 py-1.5 border border-green-300 rounded-lg bg-green-100 text-gray-900"
                    >
                      <option value="">{row.name || 'Select agent'}</option>
                      {partners.map((partner) => (
                        <option key={partner.id} value={String(partner.id)}>
                          {partner.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    row.name || '—'
                  )}
                </td>
                {canManage ? (
                  <td className="px-3 py-2 text-right">
                    {row.mandateId ? (
                      <Link
                        to={`/buildings/${buildingId}/mandates/${row.mandateId}/edit`}
                        className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-900 text-xs font-medium"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </Link>
                    ) : (
                      <Link
                        to={`/buildings/${buildingId}/mandates/new`}
                        className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-900 text-xs font-medium"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </Link>
                    )}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
